"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";

type FlipCountdownProps = {
  target: Date | string | number;
  labelClassName?: string;
  digitClassName?: string;
  className?: string;
};

type Remaining = {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
  totalMs: number;
};

function pad(n: number) {
  return n.toString().padStart(2, "0");
}

function computeRemaining(targetMs: number): Remaining {
  const now = Date.now();
  const totalMs = Math.max(0, targetMs - now);
  const totalSec = Math.floor(totalMs / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  return {
    days: pad(days),
    hours: pad(hours),
    minutes: pad(minutes),
    seconds: pad(seconds),
    totalMs,
  };
}

export default function FlipCountdown({
  target,
  labelClassName,
  digitClassName,
  className,
}: FlipCountdownProps) {
  // Hydration safety: render placeholders on server, populate after mount.
  const [mounted, setMounted] = useState(false);
  const [remaining, setRemaining] = useState<Remaining | null>(null);
  const reduce = useReducedMotion();

  const targetMs =
    target instanceof Date ? target.getTime() : new Date(target).getTime();

  useEffect(() => {
    setMounted(true);
    setRemaining(computeRemaining(targetMs));
    const id = setInterval(() => {
      setRemaining(computeRemaining(targetMs));
    }, 1000);
    return () => clearInterval(id);
  }, [targetMs]);

  const isLive = mounted && remaining !== null && remaining.totalMs <= 0;
  const isAlert =
    mounted && remaining !== null && remaining.totalMs > 0 && remaining.totalMs < 3600_000;

  if (isLive) {
    return (
      <div className={className}>
        <span className="font-display uppercase text-[var(--color-blood)] text-2xl tracking-tight">
          LIVE NOW
        </span>
      </div>
    );
  }

  const baseDigit =
    digitClassName ??
    "font-display tabular-nums text-[clamp(2.5rem,7vw,5rem)] leading-none";
  const baseLabel = labelClassName ?? "mono-strip text-[var(--color-foil)] mt-2";

  const cols: Array<{ key: string; value: string; label: string }> = mounted && remaining
    ? [
        { key: "d", value: remaining.days, label: "DAYS" },
        { key: "h", value: remaining.hours, label: "HRS" },
        { key: "m", value: remaining.minutes, label: "MIN" },
        { key: "s", value: remaining.seconds, label: "SEC" },
      ]
    : [
        { key: "d", value: "--", label: "--" },
        { key: "h", value: "--", label: "--" },
        { key: "m", value: "--", label: "--" },
        { key: "s", value: "--", label: "--" },
      ];

  const digitColor = isAlert ? "var(--color-cab)" : "var(--color-bone)";

  return (
    <div className={`flex items-start gap-4 ${className ?? ""}`}>
      {cols.map((col, idx) => (
        <div key={col.key} className="flex items-start">
          <div className="flex flex-col items-center">
            <div
              className={`${baseDigit} overflow-hidden`}
              style={{ color: digitColor, minWidth: "1.6em", textAlign: "center" }}
            >
              {reduce || !mounted ? (
                <span>{col.value}</span>
              ) : (
                <AnimatePresence mode="popLayout" initial={false}>
                  <motion.span
                    key={col.value}
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 20, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="inline-block"
                  >
                    {col.value}
                  </motion.span>
                </AnimatePresence>
              )}
            </div>
            <span className={baseLabel}>{col.label}</span>
          </div>
          {idx < cols.length - 1 && (
            <span
              className="font-display text-4xl text-[var(--color-bone-dim)] leading-none px-2 self-start"
              aria-hidden="true"
            >
              :
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
